/**
 * Wholesale Minimum Quantity Enforcement
 *
 * Enforces minimum quantity rules for wholesale customers:
 * - Reads config from #wholesale-min-qty-config element (set by Liquid)
 * - Clamps all quantity inputs to min value
 * - Intercepts AJAX cart requests (add/change/update)
 * - Prevents checkout if any line item violates min qty
 * - Increment/decrement by 1, but never below min qty
 */
(function () {
  'use strict';

  const DEBUG = true;

  function log(...args) {
    if (DEBUG) console.log('[Wholesale MinQty]', ...args);
  }

  // --- Read config from Liquid-injected element ---
  const configEl = document.getElementById('wholesale-min-qty-config');
  if (!configEl) {
    log('Config element not found — not a wholesale customer or snippet missing.');
    return;
  }

  const MIN_QTY = parseInt(configEl.dataset.minQuantity, 10) || 5;
  const IS_WHOLESALE = configEl.dataset.isWholesale === 'true';

  if (!IS_WHOLESALE) {
    log('Not a wholesale customer — no enforcement needed.');
    return;
  }

  log('Wholesale min qty applied. Min quantity:', MIN_QTY);

  // --- Utility: enforce minimum quantity (no rounding, just floor at MIN_QTY) ---
  function clampQuantity(value) {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < MIN_QTY) return MIN_QTY;
    return num;
  }

  // --- Enforce min on all quantity inputs ---
  function enforceMinOnInputs() {
    const inputs = document.querySelectorAll(
      '[data-quantity-input], input[name="quantity"], input[name="updates[]"]'
    );
    inputs.forEach(function (input) {
      input.setAttribute('min', MIN_QTY);
      input.setAttribute('step', '1');

      const currentVal = parseInt(input.value, 10);
      if (isNaN(currentVal) || currentVal < MIN_QTY) {
        input.value = MIN_QTY;
        log('Clamped input value to', MIN_QTY, input);
      }
    });
  }

  // --- Intercept +/- button clicks ---
  function handleQuantityButtons() {
    document.addEventListener('click', function (e) {
      const subtractBtn = e.target.closest('[data-subtract-quantity]');
      const addBtn = e.target.closest('[data-add-quantity]');

      if (subtractBtn) {
        const wrapper = subtractBtn.closest('[data-quantity-selector]');
        if (!wrapper) return;
        const input = wrapper.querySelector('[data-quantity-input]');
        if (!input) return;

        // Defer to allow theme JS to process first
        setTimeout(function () {
          const val = parseInt(input.value, 10);
          if (isNaN(val) || val < MIN_QTY) {
            input.value = MIN_QTY;
            log('Subtract clamped to min:', MIN_QTY);
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, 50);
      }

      if (addBtn) {
        // No special handling needed for add — theme increments by 1,
        // which is always valid above the minimum.
      }
    }, true); // Use capture to fire before theme handlers
  }

  // --- Intercept change events on quantity inputs ---
  function handleInputChange() {
    document.addEventListener(
      'change',
      function (e) {
        const input = e.target;
        if (
          input.matches &&
          (input.matches('[data-quantity-input]') ||
            input.matches('input[name="quantity"]') ||
            input.matches('input[name="updates[]"]'))
        ) {
          const val = parseInt(input.value, 10);
          if (isNaN(val) || (val > 0 && val < MIN_QTY)) {
            input.value = MIN_QTY;
            log('Input change clamped:', val, '->', MIN_QTY);
          }
        }
      },
      true
    );
  }

  // --- Intercept AJAX fetch calls to cart endpoints ---
  const originalFetch = window.fetch;
  window.fetch = function (url, options) {
    const urlStr = typeof url === 'string' ? url : url instanceof Request ? url.url : '';

    // Intercept /cart/add.js
    if (urlStr.includes('/cart/add.js') && options && options.body) {
      try {
        let body;
        if (typeof options.body === 'string') {
          body = JSON.parse(options.body);
        } else if (options.body instanceof FormData) {
          // Handle FormData
          const qty = options.body.get('quantity');
          if (qty !== null) {
            const clamped = clampQuantity(parseInt(qty, 10));
            options.body.set('quantity', clamped);
            log('AJAX /cart/add.js FormData quantity clamped to:', clamped);
          }
          return originalFetch.call(this, url, options);
        } else {
          body = options.body;
        }

        if (body && body.quantity !== undefined) {
          const clamped = clampQuantity(body.quantity);
          if (clamped !== body.quantity) {
            body.quantity = clamped;
            options.body = JSON.stringify(body);
            log('AJAX /cart/add.js quantity clamped to:', clamped);
          }
        }

        if (body && Array.isArray(body.items)) {
          body.items.forEach(function (item) {
            if (item.quantity !== undefined) {
              const clamped = clampQuantity(item.quantity);
              if (clamped !== item.quantity) {
                item.quantity = clamped;
                log('AJAX /cart/add.js items[] quantity clamped to:', clamped);
              }
            }
          });
          options.body = JSON.stringify(body);
        }
      } catch (err) {
        log('Error intercepting /cart/add.js:', err);
      }
    }

    // Intercept /cart/change.js
    if (urlStr.includes('/cart/change.js') && options && options.body) {
      try {
        let body;
        if (typeof options.body === 'string') {
          body = JSON.parse(options.body);
        } else if (options.body instanceof FormData) {
          const qty = options.body.get('quantity');
          if (qty !== null) {
            const num = parseInt(qty, 10);
            // Allow 0 (remove item), otherwise enforce min
            if (num > 0) {
              const clamped = clampQuantity(num);
              options.body.set('quantity', clamped);
              log('AJAX /cart/change.js FormData quantity clamped to:', clamped);
            }
          }
          return originalFetch.call(this, url, options);
        } else {
          body = options.body;
        }

        if (body && body.quantity !== undefined) {
          const num = parseInt(body.quantity, 10);
          // Allow 0 (remove item), otherwise enforce min
          if (num > 0) {
            const clamped = clampQuantity(num);
            if (clamped !== num) {
              body.quantity = clamped;
              options.body = JSON.stringify(body);
              log('AJAX /cart/change.js quantity clamped to:', clamped);
            }
          }
        }
      } catch (err) {
        log('Error intercepting /cart/change.js:', err);
      }
    }

    // Intercept /cart/update.js
    if (urlStr.includes('/cart/update.js') && options && options.body) {
      try {
        let body;
        if (typeof options.body === 'string') {
          body = JSON.parse(options.body);
        } else {
          body = options.body;
        }

        if (body && body.updates) {
          // updates can be an object { variant_id: qty } or array [qty, qty, ...]
          if (Array.isArray(body.updates)) {
            body.updates = body.updates.map(function (qty) {
              const num = parseInt(qty, 10);
              if (num > 0) return clampQuantity(num);
              return num; // Allow 0 (remove)
            });
          } else if (typeof body.updates === 'object') {
            Object.keys(body.updates).forEach(function (key) {
              const num = parseInt(body.updates[key], 10);
              if (num > 0) {
                body.updates[key] = clampQuantity(num);
              }
            });
          }
          options.body = JSON.stringify(body);
          log('AJAX /cart/update.js quantities enforced.');
        }
      } catch (err) {
        log('Error intercepting /cart/update.js:', err);
      }
    }

    return originalFetch.call(this, url, options);
  };

  // --- Intercept XMLHttpRequest for legacy AJAX ---
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._wholesaleUrl = url;
    return originalXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (
      this._wholesaleUrl &&
      (this._wholesaleUrl.includes('/cart/add.js') ||
        this._wholesaleUrl.includes('/cart/change.js') ||
        this._wholesaleUrl.includes('/cart/update.js'))
    ) {
      if (typeof body === 'string') {
        try {
          var parsed = JSON.parse(body);
          if (parsed.quantity !== undefined) {
            var num = parseInt(parsed.quantity, 10);
            if (num > 0) {
              parsed.quantity = clampQuantity(num);
              body = JSON.stringify(parsed);
              log('XHR', this._wholesaleUrl, 'quantity clamped to:', parsed.quantity);
            }
          }
          if (parsed.updates) {
            if (typeof parsed.updates === 'object' && !Array.isArray(parsed.updates)) {
              Object.keys(parsed.updates).forEach(function (key) {
                var n = parseInt(parsed.updates[key], 10);
                if (n > 0) parsed.updates[key] = clampQuantity(n);
              });
            }
            body = JSON.stringify(parsed);
            log('XHR', this._wholesaleUrl, 'updates enforced.');
          }
        } catch (e) {
          // Not JSON, ignore
        }
      }
    }
    return originalXHRSend.call(this, body);
  };

  // --- Prevent checkout form submission if any qty violates min ---
  function interceptCheckout() {
    document.addEventListener(
      'submit',
      function (e) {
        const form = e.target;
        if (!form || !form.matches('form')) return;

        // Check if this is a cart form heading to checkout
        const checkoutBtn = form.querySelector('[name="checkout"]');
        const isCartForm =
          form.hasAttribute('data-form') ||
          (form.action && form.action.includes('/cart'));

        if (!isCartForm) return;

        // Check all quantity inputs within the form
        const qtyInputs = form.querySelectorAll('input[name="updates[]"]');
        let hasViolation = false;

        qtyInputs.forEach(function (input) {
          const val = parseInt(input.value, 10);
          if (val > 0 && val < MIN_QTY) {
            hasViolation = true;
            input.value = MIN_QTY;
            input.style.outline = '2px solid red';
            setTimeout(function () {
              input.style.outline = '';
            }, 3000);
          }
        });

        if (hasViolation) {
          e.preventDefault();
          e.stopPropagation();
          alert(
            'Wholesale accounts require a minimum quantity of ' +
              MIN_QTY +
              ' per item. Quantities have been adjusted.'
          );
          log('Checkout blocked — quantities adjusted to meet minimum.');
          return false;
        }
      },
      true
    );
  }

  // --- Observe DOM for dynamically added quantity inputs (AJAX cart updates) ---
  function observeDynamicInputs() {
    const observer = new MutationObserver(function (mutations) {
      let hasNewInputs = false;
      mutations.forEach(function (mutation) {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) {
              if (
                node.matches &&
                (node.matches('[data-quantity-input]') ||
                  node.matches('input[name="updates[]"]') ||
                  node.matches('input[name="quantity"]'))
              ) {
                hasNewInputs = true;
              }
              if (node.querySelector) {
                const inner = node.querySelector(
                  '[data-quantity-input], input[name="updates[]"], input[name="quantity"]'
                );
                if (inner) hasNewInputs = true;
              }
            }
          });
        }
      });
      if (hasNewInputs) {
        log('New quantity inputs detected — enforcing min.');
        enforceMinOnInputs();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // --- Initialize ---
  function init() {
    enforceMinOnInputs();
    handleQuantityButtons();
    handleInputChange();
    interceptCheckout();
    observeDynamicInputs();
    log('Initialization complete.');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-enforce after AJAX cart events
  document.addEventListener('quick-cart:updated', function () {
    log('quick-cart:updated — re-enforcing min quantities.');
    setTimeout(enforceMinOnInputs, 100);
  });

  // Listen for theme internal events
  document.addEventListener('theme-internal:update-quantity-selector-counts', function () {
    log('Quantity selector counts updated — re-enforcing.');
    setTimeout(enforceMinOnInputs, 100);
  });
})();
